import { objectKeys } from "app/helpers/objects";
import type { ColumnSelectionOptions } from "app/pages/sales-intelligence/common-components/column-picker";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import type { GroupedTechnologiesCategoryMap } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import type ColumnSuggestion from "app/pages/sales-intelligence/sub-modules/column-suggestions/models/ColumnSuggestion";
import idleReduce from "app/pages/sales-intelligence/utils/idleReduce";

import { createColumnByFilterRulesEngine } from "./configuration/column-by-filter-rules-engine";
import type { FilterRulesEngine } from "./types/rules";
import type { ColumnData, InitializedServiceFields } from "./types/service";

class ColumnSuggestionsByFiltersService {
    private async initializeColumnsById(columns: ColumnSelectionOptions): Promise<Map<string, ColumnData>> {
        return idleReduce(
            columns,
            (map: Map<string, ColumnData>, column) => {
                map.set(column.id, {
                    field: column.id,
                    displayName: column.label,
                });
                return map;
            },
            new Map<string, ColumnData>(),
        );
    }

    private initializeRulesEngine(groupedTechnologies: GroupedTechnologiesCategoryMap): FilterRulesEngine {
        return createColumnByFilterRulesEngine({ groupedTechnologies });
    }

    private cleanUpSuggestedColumnNames(
        columnNames: readonly string[],
        selectedColumnNames: readonly string[],
    ): readonly string[] {
        const uniqueColumnNames = Array.from(new Set(columnNames));
        return uniqueColumnNames.filter((columnName: string) => !selectedColumnNames.includes(columnName));
    }

    private getSuggestedColumnNamesByFilter<
        K extends keyof LeadsSearchAllFiltersTypes = keyof LeadsSearchAllFiltersTypes,
    >(filterKey: K, filterDto: LeadsSearchAllFiltersTypes[K], rulesEngine: FilterRulesEngine): readonly string[] {
        if (!rulesEngine[filterKey]) {
            return [];
        }

        return rulesEngine[filterKey](filterDto);
    }

    private getSuggestedColumnNames(
        filters: Partial<LeadsSearchAllFiltersTypes>,
        selectedColumnNames: readonly string[],
        rulesEngine: FilterRulesEngine,
    ): readonly string[] {
        const suggestedColumnNamesPool = objectKeys(filters).flatMap((key) =>
            filters[key] ? this.getSuggestedColumnNamesByFilter(key, filters[key], rulesEngine) : [],
        );

        return this.cleanUpSuggestedColumnNames(suggestedColumnNamesPool, selectedColumnNames);
    }

    public async initialize(
        columns: ColumnSelectionOptions,
        groupedTechnologies: GroupedTechnologiesCategoryMap,
    ): Promise<InitializedServiceFields> {
        const [columnsById, rulesEngine] = await Promise.all([
            this.initializeColumnsById(columns),
            Promise.resolve(this.initializeRulesEngine(groupedTechnologies)),
        ]);

        return { columnsById, rulesEngine };
    }

    // Curried function factory - returns a configured function for creating suggestions
    public createColumnSuggestionsFunction(
        initializedService: InitializedServiceFields,
    ): (
        filters: Partial<LeadsSearchAllFiltersTypes>,
        selectedColumnNames: readonly string[],
    ) => readonly ColumnSuggestion[] {
        const { columnsById, rulesEngine } = initializedService;

        // Return pure function with closure over initialized data
        return (filters: Partial<LeadsSearchAllFiltersTypes>, selectedColumnNames: readonly string[]) => {
            const suggestedColumnNames = this.getSuggestedColumnNames(filters, selectedColumnNames, rulesEngine);

            return suggestedColumnNames
                .map((columnName: string) => columnsById.get(columnName))
                .filter((column): column is ColumnData => column !== undefined);
        };
    }
}

const columnSuggestionsByFiltersService = new ColumnSuggestionsByFiltersService();

export default columnSuggestionsByFiltersService;
